// I dont think it makes sense to have the types import under `features` since models are shared across features
// Thus moving to `models` folder
import {
    NRMSDocumentData,
    WorkOrderDocumentData,
} from '@/features/Aircrafts/types.js';
import {
    DiscrepancyReportDocumentData,
    TaskCardDocumentData,
} from '@/features/Documents/types.js';
import {
    AirworthinessDirective,
    AviationDocument,
    ConnectedDocument,
    DocumentStatus,
    EngineeringOrder,
    IngestionWarning,
    NonRoutineJobCardData,
    TechLogSheetData,
} from '@/features/end-of-lease/types.js';
import { DamageReport } from '@/features/maintenance/types.js';
import { FileText } from 'lucide-react';

export enum EOLIngestionStatus {
    PENDING = 'pending',
    QUEUED = 'queued',
    IN_PROGRESS = 'in_progress',
    COMPLETED = 'completed',
    FAILED = 'failed',
    REQUIRES_REINGESTION = 'requires_reingestion',
}

export interface EOLMeta {
    pageRange: {
        startPage: number;
        endPage: number;
    };
    status: DocumentStatus;
    ingestionStatus: EOLIngestionStatus;
    ingestionMessage: string | null;
    confidence: number | null;
    classificationAnalysis: string | null;
    ingestionWarnings: IngestionWarning[];
}

export enum LHTIngestionStatus {
    PENDING = 'pending',
    QUEUED = 'queued',
    IN_PROGRESS = 'in_progress',
    COMPLETED = 'completed',
    FAILED = 'failed',
    REQUIRES_REINGESTION = 'requires_reingestion',
}

export interface LHTMeta {
    ingestionStatus: LHTIngestionStatus;
    ingestionMessage: string | null;
}

export interface MissingPageRange {
    firstPage: number;
    lastPage: number;
    // The chapter these printed pages belong to, empty when the document numbers
    // its pages globally. Only AIRs carry them.
    group?: string;
    section?: string;
}

/**
 * Camelized mirror of `DocumentCompleteness` in
 * `sonance_core/models/document_completeness.py`, persisted at `docs.meta`.
 * `schemaVersion` is bumped there whenever this shape changes.
 */
export interface DocumentCompleteness {
    schemaVersion: number;
    verdict: 'complete' | 'incomplete' | 'unknown' | 'skipped';
    reason: string;
    // Independent of the verdict: the verdict says whether the pages we can see
    // are contiguous, this says whether we could have spotted a whole chapter
    // that never arrived. Optional so a payload written before it existed still
    // types.
    contentsTable?: 'not_expected' | 'present' | 'absent' | 'untrusted';
    missingPageRanges: MissingPageRange[];
    observedPageCount: number | null;
    expectedPageCount: number | null;
    markerCoverage: number | null;
}

/**
 * M4's IATA binder folder for a document (RND-312), written by the ETL at ingestion.
 *
 * Every field is optional and that is load-bearing: the key is absent on everything the
 * legacy classifier typed and on any axis whose retrieval came back empty. `confidence`
 * is the adjudicator's own hedge rather than a calibrated probability — it exists on the
 * wire and must never be rendered.
 */
export interface FolderPredictionMeta {
    code?: string | null;
    confidence?: number | null;
    analysis?: string | null;
    source?: string | null;
}

/**
 * Every folder a document is filed into, newest first. `folder` above is only
 * `folders[0]`, so reading that alone hides the rest.
 */
export interface FolderPlacementMeta {
    code?: string | null;
    source?: string | null;
    createdAt?: string | null;
}

export interface LeasingDocument {
    status: DocumentStatus;
    connectedDocuments: ConnectedDocument[] | null;
    raw: AviationDocument | null;
    ad: AirworthinessDirective | null;
    eo: EngineeringOrder | null;
    dr?: DamageReport | null;
    md?: string | null;
    document?:
        | WorkOrderDocumentData
        | NRMSDocumentData
        | TaskCardDocumentData
        | DiscrepancyReportDocumentData
        | TechLogSheetData
        | NonRoutineJobCardData
        | null;
    seen: boolean;
    revision: string | null;
    eol?: EOLMeta | null;
    folder?: FolderPredictionMeta | null;
    folders?: FolderPlacementMeta[] | null;
    lht?: LHTMeta | null;
    completeness?: DocumentCompleteness | null;
    numRowsIngested: number | null;
    numRowsFailed: number | null;
}

export type DBLeasingDocument = LeasingDocument & {
    id: number;
    teamId: number;
    fileId: number;
    leasingProjectId: number;
    createdAt: string;
    updatedAt: string;
};

export enum DocType {
    AIRWORTHINESS_DIRECTIVE = 'ad',
    AIRWORTHINESS_DIRECTIVE_STATUS_REPORT = 'adsr',
    ENGINEERING_ORDER = 'eo',
    DAMAGE_REPORT = 'dr',
    SERVICE_BULLETIN = 'sb',
    SERVICE_BULLETIN_STATUS_REPORT = 'sbsr',
    MAINTENANCE_PLANNING_DOCUMENT = 'mpd',
    STATUS_REPORT = 'sr',
    COMPONENT_MAINTENANCE_MANUAL = 'cmm',
    COMPONENT_MAINTENANCE_REPORT = 'cmr',
    AUTHORIZED_RELEASE_CERTIFICATE = 'arc',
    LIFE_PRESERVER_TEARDOWN_REPORT = 'lptr',
    WORK_ORDER = 'wo',
    AIRCRAFT_INSPECTION_REPORT = 'air',
    AIRCRAFT_READINESS_LOG = 'arl',
    AIRCRAFT_SERIALIZATION_LIST = 'asl',
    TASK_CARD = 'tc',
    NON_ROUTINE_MAINTENANCE_SHEET = 'nrms',
    NON_ROUTINE_JOB_CARD = 'NRJC',
    TECHNICAL_LOG_SHEET = 'TLS',
    AIRCRAFT_JOURNEY_LOG = 'ajl',
    DISCREPANCY_REPORT = 'dir',
    REMOVAL_INSTALLATION_LIST = 'ril',
    COMPONENT_TAG = 'ct',
    PART_MATERIAL_CERTIFICATION = 'pmc',
    REPAIR_DESIGN_APPROVAL_SHEET = 'rdas',
    WORK_REPORT = 'wr',
    UTILIZATION_REPORT = 'ur',
    UNKNOWN_TYPE = 'ut',
    LAST_DONE_NEXT_DUE = 'ldnd',
    AIRCRAFT_DELIVERY_TECH_LOGBOOK = 'adtlb',
    AIRCRAFT_MAINTENANCE_PROGRAM = 'amp',
    AIRCRAFT_FLIGHT_MANUAL = 'afm',
    SHOP_VISIT = 'sv',
    ENGINEERING_BULLETIN = 'eb',
    OCCM = 'OCCM',

    TICKET = 'TICKET',
    AIRCRAFT_LAYOVER = 'AIRCRAFT_LAYOVER',

    // Known but unhandled types
    TECHNICAL_DISPOSAL = 'technical_disposal',
    REPAIR_INSTRUCTION = 'repair_instruction',

    // Generic / uncategorized
    COMMUNICATION = 'communication',
    ENGINEERING_AUTHORIZATION = 'ea',
    FLOWCHART = 'flowchart',
    DIAGRAM = 'diagram',
    IMAGE = 'image',
}

// The manufacturer's as-delivered configuration record, one per airframer.
export const OEM_LISTING_DOC_TYPES = [
    DocType.AIRCRAFT_INSPECTION_REPORT,
    DocType.AIRCRAFT_READINESS_LOG,
    DocType.AIRCRAFT_SERIALIZATION_LIST,
] as const;

export type OemListingDocType = (typeof OEM_LISTING_DOC_TYPES)[number];

export type LeasingDocs = {
    id: number;
    /** The upload the document was split out of. Groups a bundle. */
    ingestedFileId?: number | null;
    /** Original upload filename. The only readable label when canonicalId is absent. */
    fileName?: string | null;
    canonicalId?: string;
    complianceStatus?: DocumentStatus | null;
    revision?: string;
    presignedUrl: string;
    /** The stored type code: a `DocType` member or any other label the backend returns. */
    type: string;
    links: string[];
    meta: LeasingDocument;
    leasingProjectId: number | null;
    connectedDocuments: ConnectedDocument[] | null;
    createdAt: string;
    completionStatus?: string; //Currently used for Work order completion status; optional for backwards compatibility and flexibility for other doc types
    groundTruth?: boolean;
};

type LeasingDocConfig = {
    label: string;
    icon: typeof FileText;
    badgeVariant: 'default' | 'secondary' | 'destructive';
};

type LeasingDocTypeConfigMap = {
    [K in DocType]: LeasingDocConfig;
};

export const leasingDocTypeConfig: LeasingDocTypeConfigMap = {
    [DocType.AIRWORTHINESS_DIRECTIVE]: {
        label: 'Airworthiness Directive',
        icon: FileText,
        badgeVariant: 'destructive',
    },
    [DocType.AIRWORTHINESS_DIRECTIVE_STATUS_REPORT]: {
        label: 'Airworthiness Directive Status Report',
        icon: FileText,
        badgeVariant: 'destructive',
    },
    [DocType.ENGINEERING_ORDER]: {
        label: 'Engineering Order',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.DAMAGE_REPORT]: {
        label: 'Damage Report',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.SERVICE_BULLETIN]: {
        label: 'Service Bulletin',
        icon: FileText,
        badgeVariant: 'default',
    },
    [DocType.SERVICE_BULLETIN_STATUS_REPORT]: {
        label: 'Service Bulletin Status Report',
        icon: FileText,
        badgeVariant: 'default',
    },
    [DocType.MAINTENANCE_PLANNING_DOCUMENT]: {
        label: 'Maintenance Planning Document',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.STATUS_REPORT]: {
        label: 'Status Report',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.COMPONENT_MAINTENANCE_MANUAL]: {
        label: 'Component Maintenance Manual',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.COMPONENT_MAINTENANCE_REPORT]: {
        label: 'Component Maintenance Report',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.AUTHORIZED_RELEASE_CERTIFICATE]: {
        label: 'Authorized Release Certificate',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.LIFE_PRESERVER_TEARDOWN_REPORT]: {
        label: 'Life Preserver Teardown Report',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.WORK_ORDER]: {
        label: 'Work Order',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.AIRCRAFT_INSPECTION_REPORT]: {
        label: 'Aircraft Inspection Report',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.AIRCRAFT_READINESS_LOG]: {
        label: 'Aircraft Readiness Log',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.AIRCRAFT_SERIALIZATION_LIST]: {
        label: 'Aircraft Serialization List',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.UNKNOWN_TYPE]: {
        label: 'Unrecognised Document',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.TICKET]: {
        label: 'Ticket',
        icon: FileText,
        badgeVariant: 'default',
    },
    [DocType.AIRCRAFT_LAYOVER]: {
        label: 'Aircraft Layover',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.TASK_CARD]: {
        label: 'Task Card',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.NON_ROUTINE_MAINTENANCE_SHEET]: {
        label: 'Non-Routine Maintenance Sheet',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.NON_ROUTINE_JOB_CARD]: {
        label: 'Non-Routine Job Card',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.TECHNICAL_LOG_SHEET]: {
        label: 'Technical Log Sheet',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.AIRCRAFT_JOURNEY_LOG]: {
        label: 'Aircraft Journey Log',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.DISCREPANCY_REPORT]: {
        label: 'Discrepancy Report',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.REMOVAL_INSTALLATION_LIST]: {
        label: 'Removal/Installation List',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.COMPONENT_TAG]: {
        label: 'Component Tag',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.PART_MATERIAL_CERTIFICATION]: {
        label: 'Part or Material Certification',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.REPAIR_DESIGN_APPROVAL_SHEET]: {
        label: 'Repair Design Approval Sheet',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.WORK_REPORT]: {
        label: 'Work Report',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.UTILIZATION_REPORT]: {
        label: 'Utilization Report',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.LAST_DONE_NEXT_DUE]: {
        label: 'Last Done / Next Due',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.AIRCRAFT_DELIVERY_TECH_LOGBOOK]: {
        label: 'Aircraft Delivery Tech Logbook',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.AIRCRAFT_MAINTENANCE_PROGRAM]: {
        label: 'Aircraft Maintenance Program',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.AIRCRAFT_FLIGHT_MANUAL]: {
        label: 'Aircraft Flight Manual',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.SHOP_VISIT]: {
        label: 'Shop Visit Report',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.ENGINEERING_BULLETIN]: {
        label: 'Engineering Bulletin',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.OCCM]: {
        label: 'OCCM',
        icon: FileText,
        badgeVariant: 'secondary',
    },

    // Known but unhandled types
    [DocType.TECHNICAL_DISPOSAL]: {
        label: 'Technical Disposal',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.REPAIR_INSTRUCTION]: {
        label: 'Repair Instruction',
        icon: FileText,
        badgeVariant: 'secondary',
    },

    // Generic / uncategorized
    [DocType.COMMUNICATION]: {
        label: 'Communication',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.ENGINEERING_AUTHORIZATION]: {
        label: 'Engineering Authorization',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.FLOWCHART]: {
        label: 'Flowchart',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.DIAGRAM]: {
        label: 'Diagram',
        icon: FileText,
        badgeVariant: 'secondary',
    },
    [DocType.IMAGE]: {
        label: 'Image',
        icon: FileText,
        badgeVariant: 'secondary',
    },
};

/** Human-readable label for a `DocType` code (e.g. `"wo"` -> "Work Order"), falling back to the raw code. */
export function docTypeLabel(docType: string): string {
    return leasingDocTypeConfig[docType as DocType]?.label ?? docType;
}
